import { CustomTrustpilot } from '@/components/common/CustomTrustpilot'
import { Headline2 } from '@/components/common/Headline2'
import Logo from '@/components/common/Logo'
import { Button } from '@/components/controls/Button'
import { CommitAction } from '@/components/icons/CommitAction'
import { CookieConsent } from '@/components/popups/CookieConsent'
import { SimplePreloader } from '@/components/preloader/SimplePreloader'
import Hero from '@/components/sections/Hero'
import { NavbarVariant5 } from '@/components/variants/components/NavbarVariant5'
import home_page_img from '@/public/images/site-preview.gif'
import { imageLoader } from '@/loaders/index'
import { useQuery } from '@/utils/url'
import teams_alex from '@/public/images/teams_alex.png'
import teams_peter from '@/public/images/teams_peter.png'
import graph from '@/public/images/graph.png'
import clarity from '@/public/images/clarity.png'
import deploy_coaching from '@/public/images/deploy_coaching.png'
import logos1 from '@/public/images/logos1.png'
import logos2 from '@/public/images/logos2.png'
import jock_avatar from '@/public/images/jock_avatar.png'
import { NextPage } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useBetterMediaQuery } from '@/hooks/BetterMediaQuery'
import { Quote } from '@/components/icons/Quote'
import { Question } from '@/components/icons/Question'
import { Accordion } from '@/components/accordion/Accordion'
import { SimpleCarousel, SimpleCarouselItem } from '@/components/carousel/SimpleCarousel'
import { TestimonialBig } from '@/components/common/TestimonialBig'

import blur_bg from '@/public/images/blur_bg.png'

import tino_ava from '@/public/images/tino_ava.png'
import tyler_ava from '@/public/images/tyler_ava.png'
import luke_ava from '@/public/images/luke_ava.png'
import skye_ava from '@/public/images/testim_ava1.jpg'
import testim_ava2 from '@/public/images/testim_ava2.jpg'
import testim_ava3 from '@/public/images/testim_ava3.jpg'
import testim_ava4 from '@/public/images/testim_ava4.jpg'

import { useElementWidth } from '@/hooks/ElementWidth'
import { TeamsAnimation } from '@/components/animations/TeamsAnimation'
import { NavbarWithSubmenu } from '@/components/common/NavbarWithSubmenu'
import { SEO } from '@/components/common/SEO'

const Teams: NextPage = () => {
  const freeCallLink = useQuery('/free-call', { source: 'teams' })
  const checkoutLink = useQuery(process.env.NEXT_PUBLIC_CHECKOUT_URL as string)
  const termsOfServiceLink = useQuery('/terms-of-service')
  const paymentPolicyLink = useQuery('/payment-policy')
  const privacyPolicyLink = useQuery('/privacy-policy')
  const careersLink = useQuery('/careers')
  const contactLink = useQuery('/contact')

  const sm = useBetterMediaQuery('(min-width: 768px)')
  const lg = useBetterMediaQuery('(min-width: 1024px)')
  const xl = useBetterMediaQuery('(min-width: 1280px)')
  const xl2 = useBetterMediaQuery('(min-width: 1680px)')
  const carouselContainerWidth = useElementWidth('quiz-testimonials-carousel')

  const carouselWidth = () => {
    if (xl2) {
      return carouselContainerWidth / 5 - 32
    }

    if (xl) {
      return carouselContainerWidth / 4 - 32
    }

    if (lg) {
      return carouselContainerWidth / 3 - 32
    }

    if (sm) {
      return carouselContainerWidth / 2 - 32
    }

    return carouselContainerWidth - 32
  }

  return (
    <>
      <SEO
        title="Teams"
        description="Unlock your teams full potential. As a perk of your Commit Action membership, you’ll also get the tools to put smart prioritization and accountability at the heart of your business and watch the effectiveness of your whole team transform overnight."
        canonicalUrl={process.env.NEXT_PUBLIC_SITE + '/teams/'}
        ogImageUrl={`${process.env.NEXT_PUBLIC_SITE}${home_page_img.src}`}
      />
      <div className={'bg--white color--black relative overflow-hidden'}>
        <header className={'relative'}>
          <NavbarWithSubmenu forceSmallContainer={true} pinOffset={0} darkLogo={true} darkMenu={true} darkSubmenu={false} lightBg={true}/>
        </header>
        <main className='font--accent'>
          <Hero screenHeight={false}>
            <div className="flex container flex-col md:flex-row justify-between items-center 2xl:items-end container--sec container--max-w-1111 larger:relative mt-0">
              <div className='w-full lg:w-[36%] md:mt-[20px] xl:mt-[40px] 2xl:mt-[80px] pb-[60px]'>
                <div className='flex scale-[0.8] origin-left mb-[35px]'>
                  <CustomTrustpilot stars={true} large={true} vertical={false} changePosition={true}></CustomTrustpilot>
                </div>
                <p className='text-[12px] md:text-[14px] color-secondary flex mb-[10px]'><CommitAction color={'#bca36b'}/><span className='ml-[10px]'>COMMIT ACTION TEAMS</span></p>
                <Headline2 tag="h1" weight={800} color='black' className="text-[32px] 2xl:text-[42px] !leading-[1.3] mb-[30px] max-w-[330px] md:max-w-[440px] z-10 relative">Unlock Your Team’s Full Potential</Headline2>
                <p className='md:mb-[50px]'>As a perk of your Commit Action membership, you’ll also  get the tools to put smart prioritization and accountability at the heart of your business and watch the effectiveness of your whole team transform overnight.</p>
                {sm &&
                <div className='flex flex-col'>
                  <Button href={checkoutLink} className='mr-[20px] mt-[15px] mb-[16px]' chevron={true} type="primary" noWrap={true} fullWidth={true} maxWidth={340}>Join Our Waitlist</Button>
                </div>
                }
              </div>
              <div className='w-full lg:w-[64%] relative'>
                <div className='flex flex-col ml-auto w-full md:w-[85%] relative z-[1]'>
                  <div className='mb-[-140px] md:mb-[-240px] w-[70%] relative z-[1] ml-auto'>
                    <Image loader={imageLoader} src={teams_alex} alt="Teams Alex Image" layout="intrinsic" />
                  </div>
                  <div className='opacity-[0.65] mr-[50px]'>
                    <Image loader={imageLoader} src={teams_peter} alt="Teams Peter Image" layout="intrinsic" />
                  </div>
                </div>
                <div className='absolute top-[-200px] left-[-200px] pointer-events-none'>
                  <Image loader={imageLoader} src={blur_bg} alt="Blur bg" layout="intrinsic" />
                </div>
              </div>
              {!sm &&
              <div className='flex flex-col w-full items-center'>
                <Button href={checkoutLink} className='mt-[15px] mb-[16px]' chevron={true} type="primary" noWrap={true} fullWidth={true} maxWidth={340}>Join Our Waitlist</Button>
              </div>
              }
            </div>
          </Hero>
          <section className='mt-[80px] mb-[80px] md:mt-0 2xl:mt-[80px]'>
            <div className="container container--sec container--max-w-1111">
              <div className='my-[40px]'>
                <p className='mb-[30px] font-semibold text-center lg:text-left'>Working with teams at…</p>
                <div className='flex items-center flex-col lg:flex-row'>
                  <div className='w-[90%] md:w-[60%] lg:w-full'>
                    <Image loader={imageLoader} src={logos1} alt="logos" layout="intrinsic" />
                  </div>
                  <div className='mt-[16px] lg:mt-0 lg:ml-[5%] w-[90%] md:w-[60%] lg:w-full'>
                    <Image loader={imageLoader} src={logos2} alt="logos" layout="intrinsic" />
                  </div>
                  <div>
                    <p className='flex items-center whitespace-nowrap text-[15px] lg:text-[13px] xl:text-[16px] mt-[25px] lg:mt-[0]'><span className='mx-[10px] lg:mx-[30px] text-[20px] lg:text-[25px]'>+</span>Thousands of fast growing small businesses!</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="container container--sec container--max-w-1111">
              <div className='flex flex-col md:flex-row py-[60px] md:py-[40px] lg:py-[80px] px-[32px] md:px-[40px] lg:px-[120px] mx-[-32px] md:mx-0 bg-aqua md:rounded-[20px] items-center justify-between'>
                <div className='w-full md:w-[40%]'>
                  <div className='mb-[30px]'>
                    <span className='font-bold color-secondary text-[12px]'>01</span>
                    <div className='flex mt-[5px]'>
                      <span className='w-[20px] h-[4px] bg--brown rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--gray ml-[4px] rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--gray ml-[4px] rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--gray ml-[4px] rounded-[5px]'></span>
                    </div>
                  </div>
                  <Headline2 tag="h2" weight={800} color='black' className="text-[36px] mb-[20px]">Add Team Members For Free</Headline2>
                  <p className='mb-[30px]'>As a Commit Action member you can now add free members, and share the clarity and accountability your coach creates with your whole team!</p>
                  {sm && <Button href={checkoutLink} chevron={true} type="primary" noWrap={true} fullWidth={true} maxWidth={340}>Join Our Waitlist</Button>}
                </div>
                <div className='w-[80%] md:w-[45%] flex flex-col items-center'>
                  <TeamsAnimation/>
                  {!sm && <Button href={checkoutLink} className='mt-[40px]' chevron={true} type="primary" noWrap={true} fullWidth={true} maxWidth={340}>Join Our Waitlist</Button>}
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="container container--sec container--max-w-1111">
              <div className='flex flex-col md:flex-row py-[60px] md:py-[40px] lg:py-[80px] px-[32px] md:px-[40px] lg:px-[120px] mx-[-32px] md:mx-0 justify-between items-center'>
                <div className='w-full md:w-[40%] md:order-2'>
                  <div className='mb-[30px]'>
                    <span className='font-bold color-secondary text-[12px] ml-[25px]'>02</span>
                    <div className='flex mt-[5px]'>
                      <span className='w-[20px] h-[4px] bg--brown rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--brown  ml-[4px] rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--gray ml-[4px] rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--gray ml-[4px] rounded-[5px]'></span>
                    </div>
                  </div>
                  <Headline2 tag="h2" weight={800} color='black' className="text-[30px] lg:text-[36px] mb-[20px]">Self-Guided Weekly Ritual</Headline2>
                  <p className='mb-[30px]'>Team members will be guided through Commit Actions evidence-based weekly planning ritual by our software, and commit to their highest impact tasks for the week.</p>
                </div>
                <div className='w-[80%] md:w-[45%] md:order-1'>
                  <div className='drop-shadow-2xl'>
                    <Image loader={imageLoader} src={graph} alt="graph" layout="intrinsic" />
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="container container--sec container--max-w-1111">
              <div className='flex flex-col md:flex-row py-[60px] md:py-[40px] lg:py-[80px] px-[32px] md:px-[40px] lg:px-[120px] mx-[-32px] md:mx-0 bg-aqua rounded-[20px] justify-between items-center'>
                <div className='w-full md:w-[40%]'>
                  <div className='mb-[30px]'>
                    <span className='font-bold color-secondary text-[12px] ml-[50px]'>03</span>
                    <div className='flex mt-[5px]'>
                      <span className='w-[20px] h-[4px] bg--brown rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--brown ml-[4px] rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--brown ml-[4px] rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--gray ml-[4px] rounded-[5px]'></span>
                    </div>
                  </div>
                  <Headline2 tag="h2" weight={800} color='black' className="text-[36px] mb-[20px]">Get Total Clarity & Alignment</Headline2>
                  <p className='mb-[30px]'>As the Founder account you get a full overview of your employees and their top priorities for the week. So you get total clarity and alignment across the business, and hold your team accountable for their most important work.</p>
                </div>
                <div className='w-[130%] md:w-[45%]'>
                  <div className='drop-shadow-2xl'>
                    <Image loader={imageLoader} src={clarity} alt="clarity" layout="intrinsic" />
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="container container--sec container--max-w-1111">
              <div className='flex flex-col md:flex-row py-[60px] md:py-[40px] lg:py-[80px] px-[32px] md:px-[40px] lg:px-[120px] mx-[-32px] md:mx-0  justify-between items-center'>
                <div className='w-full md:w-[40%] md:order-2'>
                  <div className='mb-[30px]'>
                    <span className='font-bold color-secondary text-[12px] ml-[75px]'>04</span>
                    <div className='flex mt-[5px]'>
                      <span className='w-[20px] h-[4px] bg--brown rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--brown ml-[4px] rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--brown ml-[4px] rounded-[5px]'></span>
                      <span className='w-[20px] h-[4px] bg--brown ml-[4px] rounded-[5px]'></span>
                    </div>
                  </div>
                  <Headline2 tag="h2" weight={800} color='black' className="text-[36px] mb-[20px]">Instantly Deploy Coaching</Headline2>
                  <p className='mb-[30px]'>Outsource structure and accountability detail. Free yourself up to focus on the vision and leadership of the business.</p>
                </div>
                <div className='w-[90%] md:w-[45%] md:order-1'>
                  <div className='drop-shadow-2xl'>
                    <Image loader={imageLoader} src={deploy_coaching} alt="deploy coaching" layout="intrinsic" />
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="container container--sec container--max-w-1111">
              <div className="flex flex-col md:flex-row lg:py-[80px] md:items-end lg:px-[120px] mt-[120px] md:mt-[60px] mb-[100px] relative">
                <div className='md:mr-[70px] mb-[30px] md:mb-0 relative'>
                  <span className='font-accent text-[130px] md:text-[200px] absolute color-secondary top-[-100px] md:top-[-160px] left-[-10px] md:left-[-15px]'>“</span>
                  <p className='text-[20px] md:text-[23px] font-light'>Going from managing projects to managing priorities and outsourcing the details has been a huge game changer for our business. Everyone is moving in the same direction, I get a high level overview of what everyone is working on, and can avoid any micromanaging. The team is loving it too, they’ve got more freedom and independence and can see how their work is having an impact on the whole business.</p>
                  <div className='absolute top-[-100px] md:top-[-200px] lg:top-[-300px] right-0 pointer-events-none'>
                    <Image loader={imageLoader} src={blur_bg} alt="Blur bg" layout="intrinsic" />
                  </div>
                </div>
                <div className='flex w-[130px] items-center'>
                  <div className='w-[60px] shrink-0'>
                    <Image loader={imageLoader} src={jock_avatar} alt="jock avatar" layout="intrinsic" />
                  </div>
                  <span className='font-bold leading-[1.2] ml-[10px]'>Jock Purtle</span>
                </div>
              </div>
            </div>
          </section>
          <section className='mb-[140px]'>
            <div className="container container--sec container--max-w-1111">
              <div className='flex flex-col items-center'>
                <Headline2 tag="h3" weight={800} color='black' className="text-[30px] md:text-[36px] mb-[20px]">The End Result…</Headline2>
                <p className='mb-[40px] md:mb-[60px] max-w-[530px] text-center'>Get your whole team aligned and working at maximum effectiveness, and watch your business growth explode.</p>
                <div className='flex w-full flex-col md:flex-row justify-center items-center'>
                  <Button href={checkoutLink} className='md:mr-[20px] mb-[16px] md:mb-0' chevron={true} type="primary" noWrap={true} fullWidth={true} maxWidth={340}>Join Our Waitlist</Button>
                </div>
              </div>
            </div>
          </section>
          <section className='mb-[60px]'>
            <div className='flex scale-[0.8] origin-center mb-[55px] justify-center'>
              <CustomTrustpilot stars={true} large={true} vertical={false} changePosition={true}></CustomTrustpilot>
            </div>
            <div id="quiz-testimonials-carousel" className='md:ml-[-16px] md:mr-[-16px]'>
              <SimpleCarousel
                loop={true}
                width={carouselWidth()}
                autoplay={false}
                animationSpeed={10000}
                doubleGap={16}
                controlsVisible={true}
                endLess={true}
                bullets={true}
                bulletsClass={'bullets--dark'}
                overflow={'hidden'}
                containerClass="checkout-testimonials-carousel2"
                nextHtml={<span className='carousel_3d_next_control--sec carousel_3d_control--light carousel_quiz_next_control'></span>}
                prevHtml={<span className='carousel_3d_prev_control--sec carousel_3d_control--light carousel_quiz_prev_control'></span>}
              >
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Skye K"
                    className="testimonial-hero-anim-less drop-shadow-xl"
                    text="In the 3 months I’ve been with Commit Action my revenue has increased by 50% and I’ve hired a team to help me execute which has bought my total hours worked down to 25 per week (while growing 2 companies)"
                    avatarUrl={skye_ava.src}
                    caption="Founder | Profit Partnerships Group"
                    height={sm ? 270 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Luke A."
                    className="testimonial-hero-anim-less drop-shadow-xl"
                    text="Before working with them I was struggling with prioritising, doing 100 hour weeks every week mainly on tasks that didn’t matter. We worked on leveraging my actions to team members, and I had my 2nd highest sales month ($20,000)..."
                    avatarUrl={luke_ava.src}
                    caption="Founder | Badass Business"
                    height={sm ? 270 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Tino T."
                    className="testimonial-hero-anim-less drop-shadow-xl"
                    text="Commit Action has been game changing for my business. I went from feeling overwhelmed and scattered with to-dos to having clarity on my yearly goals, milestones to hit, and daily tasks that would get me there. "
                    avatarUrl={tino_ava.src}
                    caption="Founder | Measured Growth"
                    height={sm ? 270 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Tyler Dudding"
                    className="testimonial-hero-anim-less drop-shadow-xl"
                    text="I have been using Commit Action for almost two months now, I’ve made more progress in my business in 2 months than in the previous 2 years, I’ve enrolled more clients than ever before and hit my highest income month ever..."
                    avatarUrl={tyler_ava.src}
                    caption="Dating Coach & Creator"
                    height={sm ? 270 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="XayLi Barclay"
                    className="testimonial-hero-anim-less drop-shadow-xl"
                    text="“Since signing up for Commit Action, I've been able to blow through my initial goals and create even bigger ones. Meeting with my coach Nick is always one of the highlights of my week!”"
                    avatarUrl={testim_ava3.src}
                    caption="Founder &amp; CEO | TDG Visual Solutions"
                    height={sm ? 270 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Khierstyn Ross"
                    className="testimonial-hero-anim-less drop-shadow-xl"
                    text="“As an entrepreneur, I constantly feel overwhelmed and second guess if what I'm working on will move me toward my goals, with Commit Action's focus, now I don't feel that way as I'm working”"
                    avatarUrl={testim_ava4.src}
                    caption="Founder &amp; CEO | Launch &amp; Scale™"
                    height={sm ? 270 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Steve Krakower"
                    className="testimonial-hero-anim-less drop-shadow-xl"
                    text="“The results speak for themselves — in the few months that I have been working working with Commit Action, our revenue has increased 25% and we finally hit our first 6-figure month”"
                    avatarUrl={testim_ava2.src}
                    caption="Founder &amp; CEO | Harbor Marketing Agency"
                    height={sm ? 270 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
              </SimpleCarousel>
            </div>
          </section>
          <section id="faq" className='py-[60px] md:py-[90px]'>
            <div className="container container--sec container--max-w-1111">
              <Headline2 tag="h2" weight={800} color='black' className="text-[28px] xl:text-[34px] 2xl:text-[36px] !leading-[1.3] mb-[30px] md:mb-[50px] max-w-[540px] z-10 relative">FAQ</Headline2>
              <div className='flex flex-col md:flex-row'>
                <div className='w-full md:w-[50%] md:pr-[70px]'>
                  <Accordion toggleBlack={true} activeSec={true} items={[
                    {
                      title: <p className='flex'><Question className='mt-[6px] mr-[10px]'/>Does Commit Action Teams cost extra on top of my coach?</p>,
                      titleSize: 20,
                      active: false,
                      itemClass: '!border-0',
                      buttonClass: '!py-[20px]',
                      text: '',
                      component: <div className='text-[14px] ml-[25px]'>
                        No, as a Commit Action coaching member, you can add as many team members as you want for free. They will get access to an app dashboard similar to yours and be guided through the weekly ritual by our software and resources. Then you will be able to easily share your goals and priorities with your team members. If you wish to upgrade your team members to have 1-to-1 coaching, that will be extra.
                      </div>
                    },
                    {
                      title: <p className='flex'><Question className='mt-[6px] mr-[10px]'/>How does the guided weekly ritual work?</p>,
                      titleSize: 20,
                      active: false,
                      itemClass: '!border-0',
                      buttonClass: '!py-[20px]',
                      text: '',
                      component: <div className='text-[14px] ml-[25px]'>
                        Our interactive dashboard will walk them through the process of creating goals, breaking them down into weekly action plans, and reviewing their progress at the same time every week.
                      </div>
                    },
                    {
                      title: <p className='flex'><Question className='mt-[6px] mr-[10px]'/>Do they get to speak to my coach?</p>,
                      titleSize: 20,
                      active: false,
                      itemClass: '!border-0',
                      buttonClass: '!py-[20px]',
                      text: '',
                      component: <div className='text-[14px] ml-[25px]'>
                        You can easily pay to add coaching at any time for your team members, either for a one-off strategy and planning call, or ongoing in the same way you do coaching.
                      </div>
                    }
                  ]}/>
                </div>
                <div className='w-full md:w-[50%] md:pl-[70px]'>
                  <Accordion toggleBlack={true} activeSec={true} items={[
                    {
                      title: <p className='flex'><Question className='mt-[6px] mr-[10px]'/>Can I keep goals / tasks private?</p>,
                      titleSize: 20,
                      active: false,
                      itemClass: '!border-0',
                      buttonClass: '!py-[20px]',
                      text: '',
                      component: <div className='text-[14px] ml-[25px]'>
                        Yes, both you and your team choose which goals & action items to share or keep private.
                      </div>
                    },
                    {
                      title: <p className='flex'><Question className='mt-[6px] mr-[10px]'/>Does my teams accounts sync with their calendars?</p>,
                      active: false,
                      itemClass: '!border-0',
                      buttonClass: '!py-[20px]',
                      titleSize: 20,
                      text: '',
                      component: <div className='text-[14px] ml-[25px]'>
                        Yes. Your teams free accounts work with the same 2-directional calendar syncing as your does. That means when they go through their weekly checkin, commit to their tasks, and block out time, they will be able to see their prior commitments on their Commit Action dashboard and build their week around them. When they’re finished, their new commitments will be pushed to their 3rd party calendar (for example, Google Calendar, iCal, Outlook etc.) so they’ve got an easy reference.
                      </div>
                    },
                    {
                      title: <p className='flex'><Question className='mt-[6px] mr-[10px]'/>What can I expect from my team using the dashboard?</p>,
                      active: false,
                      itemClass: '!border-0',
                      buttonClass: '!py-[20px]',
                      titleSize: 20,
                      text: '',
                      component: <div className='text-[14px] ml-[25px]'>
                        Additional insight into what your team is prioritizing, but beyond that a useful tool to help stimulate conversations between what is YOUR highest priority and how it effects them as well as what is your TEAM trying to achieve and how do they relate.
                      </div>
                    }
                  ]}/>
                </div>
              </div>
            </div>
          </section>
        </main>
        <footer className={'pt-8 pb-8 larger:pb-14 larger:pt-14 bg--gray-light'}>
          <div className="container container--sec flex justify-between lg:items-center">
            <nav className="order-2">
              <div className="nav nav--secondary flex">
                <div className="mr-[15px] tiny2:mr-[20px] lg:mr-0 lg:flex">
                  <div className="nav__item nav__item--secondary hover-underline-animation hover-underline-animation--black">
                    <span className="flex items-center"><span className="arrow-right"></span><Link href={termsOfServiceLink}>Terms &amp; Conditions</Link><span className="dot dot--black"></span></span>
                  </div>
                  <div className="nav__item nav__item--secondary hover-underline-animation hover-underline-animation--black">
                    <span className="flex items-center"><span className="arrow-right"></span><Link href={paymentPolicyLink}>30 Day Guarantee</Link><span className="dot dot--black"></span></span>
                  </div>
                </div>
                <div className="lg:flex">
                  <div className="nav__item nav__item--secondary hover-underline-animation hover-underline-animation--black">
                    <span className="flex items-center"><span className="arrow-right"></span><Link href={privacyPolicyLink}>Privacy Policy</Link><span className="dot dot--black"></span></span>
                  </div>
                  <div className="nav__item nav__item--secondary hover-underline-animation hover-underline-animation--black">
                    <span className="flex items-center"><span className="arrow-right"></span><Link href={careersLink}>Careers</Link><span className="dot dot--black"></span></span>
                  </div>
                  <div className="nav__item nav__item--secondary hover-underline-animation hover-underline-animation--black">
                    <span className="flex items-center"><span className="arrow-right"></span><Link href={contactLink}>Contact</Link></span>
                  </div>
                </div>
              </div>
            </nav>
            <div className="flex flex-col items-start order-1 w-[100px] xl:w-auto mr-[15px] mt-[-7px]">
              <Logo isVertical={false} color='black' className='scale-[0.74] xl:scale-[1] origin-left'/>
              <p className="site-footer__copyright site-footer__copyright--black mt-1 larger:mt-2">Commit Action © {(new Date).getFullYear()}</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

// @ts-ignore
Teams.getLayout = function getLayout(page: ReactNode) {
  return (
    <>
      {page}
      <CookieConsent/>
    </>
  )
}

export default Teams
